import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CardStats from '../../components/CardStats/CardStats';
import './home.css';
import '../../assets/css/global.scss';
import { fetchPagedTasks } from '../../services/TaskService';
import {
  getDailyStateHomeCached,
  getTribunalesUsuarioED,
} from '../../services/dailyServices';
import { getAllCourts } from '../../services/courtServices';
import { getDocumentSendStats } from '../../services/documentManagementServices';
import { getCurrentUser } from '../../services/authService';
import { useLanguage } from '../../context/LanguageContext';
import { formatDateByLocale } from '../../utils/formatDate';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { homeI18n } from './i18n';
import usePermissionsDailyState from 'permissions/modules/dailyState/usePermissionsDailyState';
import DailyStatePanelUnholster from './DailyStatePanelUnholster/DailyStatePanelUnholster';
import usePermissionsTasks from 'permissions/modules/tasks/usePermissionsTasks';

const KEYWORDS_TO_HIGHLIGHT = [
  'requerimientos',
  'notificacion',
  'embargo',
  'remate',
  'cedula',
  'resolucion',
];
const KEYWORD_PATTERN = new RegExp(
  `(${KEYWORDS_TO_HIGHLIGHT.join('|')})`,
  'gi'
);
const KEYWORD_EXACT_PATTERN = new RegExp(
  `^(${KEYWORDS_TO_HIGHLIGHT.join('|')})$`,
  'i'
);

const highlightKeywords = (text) => {
  if (!text) return text;
  const parts = text.split(KEYWORD_PATTERN);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (KEYWORD_EXACT_PATTERN.test(part)) {
      return (
        <mark key={`kw-${part}-${idx}`} className='keyword-highlight'>
          {part}
        </mark>
      );
    }
    return <span key={`text-${idx}`}>{part}</span>;
  });
};

const formatScrapedTimeByLocale = (timeString, locale) => {
  if (!timeString) return '';

  const date = new Date(`1970-01-01T${timeString}`);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const Home = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(undefined, { i18n: homeI18n });
  const { Card, SvgIcon, Button, Badge, IconButton, Skeleton } =
    DesignSystemComponents;

  const { canViewSection: canViewDailyStateSection } =
    usePermissionsDailyState();
  const { canViewSection: canViewTasksSection } = usePermissionsTasks();

  const [todayCount, setTodayCount] = useState(0);
  const [scrapedTime, setScrapedTime] = useState('');
  const [dailyPriorityCount, setDailyPriorityCount] = useState(0);
  const [dailyPendingCount, setDailyPendingCount] = useState(0);
  const [totalPendingInPjud, setTotalPendingInPjud] = useState('');
  const [isloadingDailyState, setIsLoadingDailyState] = useState(false);
  const [isIsLoadingTasks, setIsIsLoadingTasks] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [dailyState, setDailyState] = useState([]);
  const [courts, setCourts] = useState([]);
  const [tasks, setTasks] = useState({
    items: [],
    totalCount: 0,
    priorityCount: 0,
  });
  const [userId, setUserId] = useState(null);
  const [userFirstName, setUserFirstName] = useState('');
  const [filters, setFilters] = useState(null);
  const homeLanguage = normalizeSupportedLanguage(language);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);

      if (authUser?.name) {
        const first = authUser.name.trim().split(' ')[0];
        setUserFirstName(first);
      }
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    setFilters({
      UserId: userId,
      SearchDailyState: '',
      HasDocument: true,
      Courts: [],
      StartDate: null,
      EndDate: null,
      IsActive: null,
      Role: '',
      Case: '',
      Process: [],
      States: [1],
      MyCases: 'todos',
    });
  }, [userId]);

  const fetchTasks = async () => {
    setIsIsLoadingTasks(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const result = await fetchPagedTasks({
        pageNumber: 1,
        pageSize: 10,
        sortBy: 'fechaVencimiento',
        sortDirection: 'DESC',
        usuarioAsignadoId: userId,
        fechaVencimiento: today,
        statusId: 2,
      });

      const priorityOrder = {
        Alta: 1,
        Media: 2,
        Baja: 3,
      };

      const todayItems = (result.items || [])
        .filter(
          (item) =>
            item.fechaVencimiento &&
            item.fechaVencimiento.split('T')[0] === today
        )
        .sort((a, b) => {
          const pa = priorityOrder[a.priority] ?? 4;
          const pb = priorityOrder[b.priority] ?? 4;
          return pa - pb;
        });

      setTasks({
        items: todayItems,
        totalCount: todayItems.length,
        priorityCount: result.priorityCount,
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsIsLoadingTasks(false);
    }
  };

  const fetchDocumentSendStats = async () => {
    setIsLoadingDocs(true);
    try {
      const result = await getDocumentSendStats.getStats(userId);
      setTotalPendingInPjud(result.totalPendingInPjud);
    } catch {
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const fetchCourts = async () => {
    const resp = await getAllCourts();
    setCourts(resp);
  };

  useEffect(() => {
    homeI18n.changeLanguage(homeLanguage);
  }, [homeLanguage]);

  const handleDailyStateCounters = useCallback(({ urgentes, pendientes }) => {
    setDailyPriorityCount(urgentes);
    setDailyPendingCount(pendientes);
    setIsLoadingDailyState(false);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const loadDailyStateFlow = async (userId, baseFilters) => {
    setIsLoadingDailyState(true);

    try {
      const resp = await getTribunalesUsuarioED(userId);

      const tribunalIds = Array.isArray(resp?.tribunalIds)
        ? resp.tribunalIds.map(Number).filter(Number.isFinite)
        : [];

      const finalFilters = {
        ...baseFilters,
        Courts: tribunalIds,
        MyCases: tribunalIds.length > 0 ? 'todos' : 'mis causas',
      };

      const all = await getDailyStateHomeCached(finalFilters);

      const priorityItems = all.slice(0, all[0].priorityCount);
      const todayItems = all.slice(0, all[0].todayCount);

      const merged = [...priorityItems, ...todayItems].filter(
        (item, index, arr) =>
          index === arr.findIndex((x) => x.code === item.code)
      );

      setDailyState(merged);
      setTodayCount(all[0].todayCount);
      setDailyPriorityCount(all[0].priorityCount);
      setDailyPendingCount(all[0].pendingCount);
      setScrapedTime(
        formatScrapedTimeByLocale(all[0].lastScrapedDate, homeLanguage)
      );
    } catch (error) {
      console.error('Error cargando DailyState Home', error);
    } finally {
      setIsLoadingDailyState(false);
    }
  };

  // El panel nuevo carga sus propios datos y reporta los contadores.
  // useEffect(() => {
  //   if (!userId || !filters) return;
  //   loadDailyStateFlow(userId, filters);
  // }, [userId, filters, homeLanguage]);

  useEffect(() => {
    if (!userId) return;

    const loadIndependentData = async () => {
      setIsIsLoadingTasks(true);
      setIsLoadingDocs(true);

      try {
        await Promise.all([
          fetchTasks(),
          fetchDocumentSendStats(),
          fetchCourts(),
        ]);
      } catch (e) {
        console.error('Error cargando datos secundarios del Home', e);
      } finally {
        setIsIsLoadingTasks(false);
        setIsLoadingDocs(false);
      }
    };

    loadIndependentData();
  }, [userId]);

  function getPriorityColor(priority) {
    if (!priority) return 'neutral';

    const p = priority.trim().toLowerCase();
    if (p === 'alta') return 'error';
    if (p === 'media') return 'warning';
    if (p === 'baja') return 'success';

    return 'neutral';
  }

  const getGreetingKey = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return 'home_greeting_morning';
    }

    if (hour >= 12 && hour < 19) {
      return 'home_greeting_afternoon';
    }

    return 'home_greeting_evening';
  };

  const greetingKey = getGreetingKey();

  if (!filters) return null;

  return (
    <>
      <div className='mb-4'>
        <h2 className='fw-semibold'>
          {t(greetingKey)}
          {userFirstName ? `, ${userFirstName}` : ''}
        </h2>
        <h4 className=' fw-normal'>{t('home_subtitle')}</h4>
      </div>
      <div className='row mb-4'>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('home_priorityCases')}
            count={dailyPriorityCount}
            subtitle={t('home_priority')}
            icon='exclamationTriangle'
            fill='var(--error-500)'
            type='error'
            loading={isloadingDailyState}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('home_updatedCases')}
            count={dailyPendingCount}
            subtitle={t('home_lastHours')}
            icon='briefcase'
            loading={isloadingDailyState}
          />
        </div>

        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('home_tasks')}
            count={tasks.totalCount}
            subtitle={t('home_pendingToday')}
            icon='clock'
            loading={isIsLoadingTasks}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('home_documents')}
            count={totalPendingInPjud}
            subtitle={t('home_pending')}
            icon='fileText'
            loading={isLoadingDocs}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-12 col-md-6'>
          {/* Panel sobre la API de Unholster. El bloque viejo leia info_causas_civil,
              congelada desde julio. Para volver atras: descomentar lo de abajo. */}
          <DailyStatePanelUnholster
            DesignSystemComponents={DesignSystemComponents}
            userId={userId}
            canView={canViewDailyStateSection}
            onCounters={handleDailyStateCounters}
          />
          {/*
          // <Card className='card-state overflow-hidden' notPadding>
          // <div className='d-flex align-items-center justify-content-between pt-4 px-4'>
          // <div className='d-flex align-items-center'>
          // <h4 className='fw-normal  me-3'>{t('home_dailyState')}</h4>
          // <p className='mini fw-normal font-light'>
          // {scrapedTime && t('home_updated')} {scrapedTime}
          // </p>
          // </div>
          // {canViewDailyStateSection && (
          // <Link to='/estado-diario' className='text-decoration-none'>
          // <Button
          // iconEnd='arrowRightShort'
          // label={t('home_seeAll')}
          // size='sm'
          // variant='naked'
          // />
          // </Link>
          // )}
          // </div>
          // <hr className='line mt-4 mb-4' />
          // <div className='ps-4 pe-2 pb-4 overflow-auto long-card-content-left'>
          // {isloadingDailyState ? (
          // [...Array(10)].map((_, i) => (
          // <div key={i} className='mb-3'>
          // <Skeleton height={60} />
          // </div>
          // ))
          // ) : dailyState.length > 0 && canViewDailyStateSection ? (
          // dailyState?.map((item, i) => (
          // <Link
          // key={i}
          // to={`/detalle-causa/${item.id}`}
          // state={{ caseData: item, lawyers: [], courts }}
          // className={`container-like-card like-card-left d-flex align-items-center justify-content-between cursor-pointer text-decoration-none ${
          // i < dailyPriorityCount ? 'urgent-update' : ''
          // }`}
          // >
          // <div className='d-flex align-items-center flex-grow-1 overflow-hidden'>
          // <div className='d-flex flex-column me-4 flex-grow-1 overflow-hidden'>
          // <p className='body  mb-2 text-truncate'>{item.title}</p>
          // <div className='d-flex align-items-center overflow-hidden'>
          // <SvgIcon
          // className='me-2 flex-shrink-0'
          // size='1.125rem'
          // name='fileText'
          // stroke='var(--font-light)'
          // />
          // <div className='d-flex overflow-hidden'>
          // <p className='mini fw-semibold mb-0 flex-shrink-0 me-1 text-nowrap font-light'>
          // {highlightKeywords(item.process)}
          // </p>
          // <p className='mini fw-normal mb-0 text-truncate font-light'>
          // {highlightKeywords(item.description)}
          // </p>
          // </div>
          // </div>
          // </div>
          // <div className='me-4 flex-shrink-0'>
          // <Badge
          // autoWidth
          // color='neutral'
          // size='sm'
          // text={item.code}
          // className='mb-2 ms-2'
          // />
          // <div className='d-flex align-items-center'>
          // <SvgIcon
          // className='me-2 flex-shrink-0'
          // size='1rem'
          // name='calendar'
          // stroke='var(--font-light)'
          // fill='var(--font-light)'
          // />
          // <p className='mini fw-normal font-light mb-0'>
          // {formatDateByLocale(item.date, homeLanguage)}
          // </p>
          // </div>
          // </div>
          // </div>
          // <IconButton ariaLabel='Ir' icon='chevronRight' size='lg' />
          // </Link>
          // ))
          // ) : (
          // <>
          // <p className='text-center py-4'>{t('home_noDailyStates')}</p>
          // <p className='mini fw-normal font-light text-center'>
          // {t('home_noDailyStatesSubtitle')}
          // </p>
          // </>
          // )}
          // </div>
          // </Card>
          */}
        </div>
        {/* Tasks */}
        <div className='col-12 col-md-6'>
          <Card className='card-state overflow-hidden' notPadding>
            <div className='d-flex align-items-center justify-content-between pt-4 px-4'>
              <div className='d-flex align-items-center'>
                <h4 className='fw-normal  me-3'>{t('home_pendingTasks')}</h4>
                <p className='mini fw-normal font-light'>
                  {t('home_todayTasks')}
                </p>
              </div>
              {canViewTasksSection && (
                <Link to='/tareas' className='text-decoration-none'>
                  <Button
                    iconEnd='arrowRightShort'
                    label={t('home_seeAllTasks')}
                    size='sm'
                    variant='naked'
                  />
                </Link>
              )}
            </div>
            <hr className='line mt-4 mb-4' />
            <div className='ps-4 pe-2 pb-4 overflow-auto long-card-content-right'>
              {isIsLoadingTasks ? (
                [...Array(10)].map((_, i) => (
                  <div key={i} className='mb-3'>
                    <Skeleton height={60} />
                  </div>
                ))
              ) : tasks.items.length > 0 && canViewTasksSection ? (
                tasks.items.map((task, i) => (
                  <Link
                    key={task.id}
                    to={`/detalle-causa/${task.caseId}/tareas`}
                    state={{ taskIdToOpen: task.id }}
                    className={`container-like-card like-card-left d-flex align-items-center justify-content-between cursor-pointer text-decoration-none ${
                      i < tasks.priorityCount ? 'urgent-update' : ''
                    }`}
                  >
                    <div className='d-flex flex-column flex-grow-1'>
                      <div className='d-flex align-items-center justify-content-between'>
                        <div className='d-flex'>
                          <p className='mini fw-semibold  me-2'>
                            {task.nombreTarea}
                          </p>
                          <p className='mini fw-normal font-light'>
                            {task.casoRol || '-'}
                          </p>
                        </div>
                        <Badge
                          autoWidth
                          size='sm'
                          text={task.priority || t('home_noPriority')}
                          color={getPriorityColor(task.priority)}
                          className='mb-2 ms-2'
                        />
                      </div>

                      <div className='d-flex align-items-center'>
                        <SvgIcon
                          className='me-2 flex-shrink-0'
                          size='1rem'
                          name='clock'
                        />
                        <p className='mini fw-medium font-light mb-0'>
                          {task.fechaVencimiento
                            ? formatDateByLocale(
                                task.fechaVencimiento,
                                homeLanguage
                              )
                            : '--'}
                        </p>
                      </div>
                    </div>
                    <IconButton ariaLabel='Ir' icon='chevronRight' size='lg' />
                  </Link>
                ))
              ) : (
                <p className='text-center py-4'>{t('home_noTasks')}</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Home;
